import React, { useEffect, useRef, useState } from 'react'
import { ExpandMore } from '@mui/icons-material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import { Box, IconButton, Typography, useTheme } from '@mui/material'
import mapboxgl from 'mapbox-gl'

import { CheckIcon, TrashIcon } from '~assets/icons'
import { MAPBOX_TOKEN } from '~config'
import { Address0, GeoPoint } from '~types'

interface AddressDisplayProps {
  address?: Address0
  location: GeoPoint
  removable?: boolean
  onRemove?: () => void
  readOnly?: boolean
  showCopyButton?: boolean
}

const AddressDisplay: React.FC<AddressDisplayProps> = ({
  address,
  location,
  removable = false,
  onRemove,
  readOnly = false,
  showCopyButton = false,
}) => {
  const [expanded, setExpanded] = useState(false)
  const [copied, setCopied] = useState(false)
  const theme = useTheme()
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<mapboxgl.Map | null>(null)
  const googleMapsIconUrl =
    'https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/googleMaps.png'
  const appleMapsIconUrl =
    'https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/appleMaps.png'

  const displayAddress = address
    ? `${address.street}, ${address.city}, ${address.state} ${address.zip}`
    : location.lon === 0 && location.lat === 0
      ? 'Loading address...'
      : `(${location.lon}, ${location.lat})`

  const handleCopyAddress = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(displayAddress)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy text:', err)
    }
  }

  const getGoogleMapsUrl = (): string => {
    if (address) {
      return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(displayAddress)}`
    }
    return `https://www.google.com/maps/search/?api=1&query=${location.lat},${location.lon}`
  }

  const getAppleMapsUrl = (): string => {
    if (address) {
      return `http://maps.apple.com/?address=${encodeURIComponent(displayAddress)}`
    }
    return `http://maps.apple.com/?ll=${location.lat},${location.lon}`
  }

  useEffect(() => {
    if (expanded && mapContainer.current && !map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/streets-v11',
        center: [location.lon, location.lat],
        zoom: 12,
        accessToken: MAPBOX_TOKEN,
      })

      new mapboxgl.Marker().setLngLat([location.lon, location.lat]).addTo(map.current)
    }

    return (): void => {
      if (map.current) {
        map.current.remove()
        map.current = null
      }
    }
  }, [expanded, location])

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: readOnly ? theme.palette.action.disabledInput : 'transparent',
        border: readOnly ? `1px solid ${theme.palette.text.disabled}` : 'none',
        borderRadius: readOnly ? 1 : 0,
        padding: readOnly ? 1.5 : 0,
        minHeight: readOnly ? '28px' : 'auto',
        cursor: readOnly ? 'default' : 'auto',
      }}
      onClick={readOnly ? (): void => setExpanded(!expanded) : undefined}
    >
      <Box display='flex' alignItems='center' gap={1}>
        <Typography
          onClick={readOnly ? undefined : handleCopyAddress}
          sx={{
            cursor: readOnly ? 'default' : 'pointer',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            flexGrow: 1,
            color: readOnly ? theme.palette.text.secondary : theme.palette.text.primary,
            '&:hover': readOnly ? {} : { textDecoration: 'underline' },
          }}
        >
          {displayAddress}
        </Typography>
        {readOnly && showCopyButton && (
          <IconButton
            onClick={(e) => {
              e.stopPropagation()
              handleCopyAddress()
            }}
            size='small'
            aria-label='copy address'
            sx={{ padding: 0.5 }}
          >
            {copied ? <CheckIcon width={16} /> : <ContentCopyIcon fontSize='small' />}
          </IconButton>
        )}
        <IconButton onClick={() => setExpanded(!expanded)}>
          <ExpandMore sx={{ transform: expanded ? 'rotate(180deg)' : 'none' }} />
        </IconButton>
      </Box>

      {expanded && (
        <Box
          sx={{
            mt: 2,
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'grey.200',
            py: 1,
          }}
        >
          <Box
            ref={mapContainer}
            sx={{
              height: 150,
              width: '100%',
              borderRadius: 1,
              mb: 2,
            }}
          />

          <Box display='flex' alignItems='center' gap={1} mb={2}>
            <Typography fontWeight='bold'>View On:</Typography>
            <a
              href={getGoogleMapsUrl()}
              className='underline-on-hover'
              target='_blank'
              rel='noopener noreferrer'
              style={{
                textDecoration: 'none',
                display: 'flex',
                alignItems: 'center',
                marginRight: '10px',
              }}
            >
              <img
                src={googleMapsIconUrl}
                alt='Google Maps'
                width={14}
                style={{ marginRight: '4px' }}
              />
              Google Maps
            </a>
            <a
              href={getAppleMapsUrl()}
              className='underline-on-hover'
              target='_blank'
              rel='noopener noreferrer'
              style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
            >
              <img
                src={appleMapsIconUrl}
                alt='Apple Maps'
                width={14}
                style={{ marginRight: '4px' }}
              />
              Apple Maps
            </a>
          </Box>

          {address && (
            <>
              <Box mb={2}>
                <Typography fontWeight='bold' component='span'>
                  Address Line:{' '}
                </Typography>
                <Typography component='span'>{address.street}</Typography>
              </Box>
              <Box mb={2}>
                <Typography fontWeight='bold' component='span'>
                  City:{' '}
                </Typography>
                <Typography component='span'>{address.city}</Typography>
              </Box>
              <Box mb={2}>
                <Typography fontWeight='bold' component='span'>
                  State:{' '}
                </Typography>
                <Typography component='span'>{address.state}</Typography>
              </Box>
              <Box mb={2}>
                <Typography fontWeight='bold' component='span'>
                  Zip Code:{' '}
                </Typography>
                <Typography component='span'>{address.zip}</Typography>
              </Box>
            </>
          )}

          <Box mb={2}>
            <Typography fontWeight='bold' component='span'>
              Coordinates:{' '}
            </Typography>
            <Typography component='span'>{`(${location.lon}, ${location.lat})`}</Typography>
          </Box>

          {removable && onRemove && (
            <Box display='flex' alignItems='center' sx={{ cursor: 'pointer' }} onClick={onRemove}>
              <TrashIcon width={12} />
              <Typography fontSize={14} ml={1} sx={{ '&:hover': { textDecoration: 'underline' } }}>
                Remove Address
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  )
}

export default AddressDisplay
